import { Component, h, Prop, Watch, Host, JSX, Fragment, Listen, State, Element } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { CmsImage, CmsLink, TrackableContentId } from '../../types/global';
import { validateVariant } from './validate-variants';

type ViewportSizes = 0 | 600 | 1200;

interface CmsImageSuperteaser extends CmsImage {
  mobileAspectRatio?: '1:1' | '4:3';
}

export interface UiCmsSuperteaserBody extends TrackableContentId {
  variant: 'small' | 'medium' | 'large';
  textPosition: 'left-center' | 'left-bottom' | 'right-center' | 'center-bottom';
  brandshapeColor: 'white' | 'primary';
  brandshapeOrientation: 'behind-text' | 'behind-claim' | 'hide';
  textType: 'text' | 'quotation';
  mediaType: 'image' | 'moving-image';
  overline?: string;
  headline?: string;
  subline?: string;
  /** claim is never visible on mobile */
  claim?: string;
  link?: CmsLink;
  quotationAuthor?: string;
  quotationAuthorInfo?: string;
  quotationText?: string;
  quotationStyle?: 'large';
  quotationSubtitle?: string;
  quotationFont?: 'default' | 'handwriting'; // handwriting = Ego
  /** textColor has no effect on mobile */
  textColor?: 'white' | 'primary';
  textShadow?: boolean;
  image?: CmsImageSuperteaser;
  movingImageSrc?: string;
  copyrightText?: string;
  copyrightColor?: 'black' | 'white';
}

@Component({
  tag: 'ui-cms-superteaser',
  styleUrl: 'ui-cms-superteaser.scss',
  shadow: false,
})
export class UiCmsSuperteaser {
  @Element() el: HTMLUiSuperteaserElement;

  @Prop({ mutable: true }) body: UiCmsSuperteaserBody = {
    variant: 'medium',
    textPosition: 'left-center',
    brandshapeColor: 'primary',
    brandshapeOrientation: 'behind-text',
    textType: 'text',
    mediaType: 'image',
  };

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @State() viewport: ViewportSizes;

  @Listen('resize', { target: 'window' })
  handleResize(): void {
    this.viewport = this.getViewportSize();
    this.setBrandshapeOffset();
  }

  private brandshapeElem: HTMLElement;

  private getViewportSize(): ViewportSizes {
    // TODO: refactor to use this.el.getBoundingClientRect().width instead of window.innerWidth
    // Currently not working because the element width is not calculated correctly inside a slider
    const width = window.innerWidth;
    if (width >= 600 && width < 1200) {
      return 600;
    }
    if (width >= 1200) {
      return 1200;
    }
    return 0;
  }

  private get shapes(): string[] {
    const { brandshapeOrientation, textPosition } = this.body;

    const textPositionMap: Record<string, string> = {
      'left-center': 'leftCenter',
      'left-bottom': 'leftBottom',
      'right-center': 'rightCenter',
      'center-bottom': 'centerBottom',
    };

    if (brandshapeOrientation === 'behind-claim') return ['top'];

    const behindTextMap: Record<string, string[]> = {
      leftBottom: this.viewport === 0 ? ['middle', 'bottom'] : ['top', 'middle'],
      centerBottom: this.viewport === 0 ? ['middle', 'bottom'] : ['top', 'middle'],
    };

    return behindTextMap[textPositionMap[textPosition]] || ['top', 'middle', 'bottom'];
  }

  private get hasContent(): boolean {
    const { overline, headline, subline, link, quotationText, quotationAuthor, quotationAuthorInfo } = this.body;
    return !!(overline || headline || subline || link?.src || quotationText || quotationAuthor || quotationAuthorInfo);
  }

  private setBrandshapeOffset(): void {
    if (!this.brandshapeElem) return;

    const isSmallViewport = this.viewport < 1200;
    const isBehindText = this.body.brandshapeOrientation === 'behind-text';
    const isLeftCenter = this.body.textPosition === 'left-center';

    if (isSmallViewport && isBehindText && isLeftCenter) {
      const offset = 48;
      (this.brandshapeElem as any).updateComplete.then(() => {
        const shapeTop = this.brandshapeElem.shadowRoot?.querySelector('[part="shape-top"]');
        const height = shapeTop?.getBoundingClientRect().height || 50;
        this.brandshapeElem.style.marginTop = `-${height + offset}px`;
      });
    } else {
      this.brandshapeElem.style.marginTop = '0px';
    }
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    validateVariant(this.body);
    this.viewport = this.getViewportSize();
  }

  componentDidLoad() {
    this.setBrandshapeOffset();
  }

  private handleLinkClick(): void {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Superteaser',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Teaser',
        elementLabel: this.body.link.title,
        target: this.body.link.contentId,
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  private renderImage(): JSX.Element {
    const { copyrightText, copyrightColor, image } = this.body;
    const { objectPosition, alt, src, mobileSrc, mobileAspectRatio } = image || {};

    const imageClasses = {
      st__image: true,
      'st__image--mobile-aspect-ratio-square': mobileAspectRatio === '1:1',
    };

    return (
      <div
        class={{ 'sd-copyright': true, [`sd-copyright--color-${copyrightColor}`]: true }}
        style={{ '--copyright': copyrightText ? `'© ${copyrightText}'` : '' }}
      >
        <ui-cms-image
          class={imageClasses}
          size={2400}
          src={this.viewport === 0 && mobileSrc ? mobileSrc : src}
          alt={alt}
          loading="eager"
          objectPosition={objectPosition}
        ></ui-cms-image>
      </div>
    );
  }

  private renderMovingImage(): JSX.Element {
    return (
      <video class="superteaser__moving-image" autoPlay muted loop playsInline preload="auto">
        <source src={this.body.movingImageSrc.replace('.mp4', '.webm')} type="video/webm"></source>
        <source src={this.body.movingImageSrc} type="video/mp4" />
      </video>
    );
  }

  private renderBrandshape(content: JSX.Element): JSX.Element {
    return (
      <sd-brandshape
        class="st__bs"
        ref={(el: HTMLElement) => {
          this.brandshapeElem = el;
        }}
        variant={this.body.brandshapeColor}
        shapes={this.shapes}
      >
        {content}
      </sd-brandshape>
    );
  }

  private renderContent(): JSX.Element {
    if (this.body.textType === 'quotation') return this.renderQuotation();
    return this.renderText();
  }

  private renderLink(): JSX.Element {
    const { src, icon, target, title, ariaLabel } = this.body.link;
    return (
      <sd-button variant="cta" class="st__link" href={src} target={target} onClick={() => this.handleLinkClick()}>
        {icon && <sd-icon library={window.ui.cmsState.iconLibrary} name={icon} slot="icon-left"></sd-icon>}
        <span aria-hidden="true">{title}</span>
        <span class="sr-only">{ariaLabel}</span>
      </sd-button>
    );
  }

  private renderText(): JSX.Element {
    const { overline, headline, subline, link } = this.body;
    return (
      <Fragment>
        {overline && <p class="st__overline sd-headline sd-headline--size-lg">{overline}</p>}
        {headline && <p class="st__headline sd-headline">{headline}</p>}
        {subline && <p class="st__subline sd-headline sd-headline--size-lg">{subline}</p>}
        {link?.src && this.renderLink()}
      </Fragment>
    );
  }

  private renderQuotation(): JSX.Element {
    const {
      quotationText,
      quotationStyle,
      quotationAuthor,
      quotationAuthorInfo,
      quotationFont,
      quotationSubtitle,
      link,
    } = this.body;

    return (
      <div class="st__quotation">
        <q
          class={{
            'sd-headline': true,
            'sd-headline--size-3xl': !quotationStyle,
            'st__quotation--handwriting': quotationFont === 'handwriting',
          }}
        >
          {quotationText}
        </q>
        <p class="sd-paragraph st__quotation-author">{quotationAuthor}</p>
        <p class="sd-paragraph sd-paragraph--size-sm">{quotationAuthorInfo}</p>
        {quotationSubtitle && <p class="sd-paragraph st__quotation-subtitle">{quotationSubtitle}</p>}
        {link?.src && this.renderLink()}
      </div>
    );
  }

  private renderClaim(): JSX.Element {
    return this.body.claim && <p class="st__claim-text sd-headline sd-headline--size-lg">{this.body.claim}</p>;
  }

  render() {
    const stClasses = {
      st: true,
      [`st--variant-${this.body.variant}`]: !!this.body.variant,
      [`st--text-position-${this.body.textPosition}`]: !!this.body.textPosition,
      [`st--text-color-${this.body.textColor}`]: !!this.body.textColor,
      'st--text-shadow': this.body.textShadow,
      [`st--bs-color-${this.body.brandshapeColor}`]: !!this.body.brandshapeColor,
      [`st--bs-orientation-${this.body.brandshapeOrientation}`]: !!this.body.brandshapeOrientation,
    };

    return (
      <Host>
        <div class={stClasses}>
          <div class="st__background">
            {this.body.mediaType === 'image' && this.renderImage()}
            {this.body.mediaType === 'moving-image' && this.renderMovingImage()}
            {this.body.textPosition !== 'center-bottom' && (
              <div class="st__claim">
                {this.body.brandshapeOrientation === 'behind-claim'
                  ? this.renderBrandshape(this.renderClaim())
                  : this.renderClaim()}
              </div>
            )}
          </div>
          {this.hasContent && (
            <div class="st__content">
              <div class="st__content-wrapper">
                {this.body.brandshapeOrientation === 'behind-text'
                  ? this.renderBrandshape(this.renderContent())
                  : this.renderContent()}
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
